import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articleContent, allArticles, categories } from '@/mocks/zh/article';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContent[slug] : undefined;

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return article.relatedSlugs
      .map((s) => allArticles.find((a) => a.slug === s))
      .filter(Boolean) as typeof allArticles;
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
          <div className="text-center py-20 px-4">
            <div className="w-20 h-20 mx-auto mb-6 bg-background-100 rounded-full flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-8">这个瓜可能已经被吃完了，换个看看吧</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-foreground-950 font-semibold rounded-full text-sm hover:bg-accent-600 transition-colors">
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryObj = categories.find((c) => c.slug === article.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    url: `https://www.51heiliao.com/article/${slug}`,
    publisher: {
      '@type': 'Organization',
      name: '51黑料网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.51heiliao.com/article/${slug}`,
    },
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-20">
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 bg-accent-500 text-foreground-950 text-xs font-semibold rounded-full">
                {article.categoryName}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-500 text-xs rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 md:gap-6 text-sm text-foreground-400 border-b border-background-200 pb-6 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万` : article.views} 阅读
              </span>
              {categoryObj && (
                <span className="flex items-center gap-1">
                  <i className={categoryObj.icon}></i> {categoryObj.name}
                </span>
              )}
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
            style={{
              '--tw-prose-body': 'oklch(var(--foreground-800))',
              '--tw-prose-headings': 'oklch(var(--foreground-950))',
              '--tw-prose-links': 'oklch(var(--accent-600))',
              '--tw-prose-bold': 'oklch(var(--foreground-950))',
            } as React.CSSProperties}
          />

          <div className="border-t border-background-200 pt-8 mb-12">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-foreground-700">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-background-100 text-foreground-600 text-sm rounded-full hover:bg-accent-100 hover:text-accent-700 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mb-16">
              <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关爆料</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group flex gap-4 bg-background-50 rounded-xl border border-background-200 hover:border-accent-300 p-4 transition-all duration-300"
                  >
                    <div className="w-28 h-28 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-accent-600 font-medium">{ra.categoryName}</span>
                      <h4 className="text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-accent-700 transition-colors leading-snug">
                        {ra.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                        <span>{ra.date}</span>
                        <span>{ra.views >= 10000 ? `${(ra.views / 10000).toFixed(0)}万` : ra.views} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}